import { useState } from "react";
import { Loader2, Mail, Lock, User, Phone } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Separator } from "./ui/separator";
import { authAPI } from "../lib/api";

export default function AuthModal({ open, onClose, onAuth }) {
  const [tab, setTab] = useState("login");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({ name: "", email: "", password: "", phone: "" });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await authAPI.login(loginForm);
      localStorage.setItem("slabstory_token", res.data.token);
      onAuth(res.data.user);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await authAPI.register(regForm);
      localStorage.setItem("slabstory_token", res.data.token);
      onAuth(res.data.user);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm rounded-2xl" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-center text-xl">Welcome to SlabStory</DialogTitle>
        </DialogHeader>

        {/* Google Sign In */}
        <Button
          variant="outline"
          className="w-full rounded-xl h-11 border-slate-200 hover:bg-slate-50 font-medium"
          onClick={handleGoogleLogin}
          data-testid="google-signin-btn"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Continue with Google
        </Button>

        <div className="flex items-center gap-3 my-1">
          <Separator className="flex-1" />
          <span className="text-xs text-slate-400">or</span>
          <Separator className="flex-1" />
        </div>

        {error && <p className="text-red-500 text-xs text-center bg-red-50 p-2 rounded-lg" data-testid="auth-error">{error}</p>}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full bg-slate-100 rounded-xl p-1">
            <TabsTrigger value="login" className="flex-1 rounded-lg text-sm data-[state=active]:bg-white" data-testid="auth-tab-login">Sign In</TabsTrigger>
            <TabsTrigger value="register" className="flex-1 rounded-lg text-sm data-[state=active]:bg-white" data-testid="auth-tab-register">Sign Up</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <form onSubmit={handleLogin} className="space-y-3 pt-2">
              <div>
                <Label className="text-xs">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    placeholder="you@email.com" type="email" required className="pl-10 rounded-xl" data-testid="login-email" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    placeholder="Password" type="password" required className="pl-10 rounded-xl" data-testid="login-password" />
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-terracotta hover:bg-terracotta-hover text-white rounded-xl h-11" data-testid="login-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Sign In
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="register">
            <form onSubmit={handleRegister} className="space-y-3 pt-2">
              <div>
                <Label className="text-xs">Full Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input value={regForm.name} onChange={(e) => setRegForm({ ...regForm, name: e.target.value })}
                    placeholder="Your name" required className="pl-10 rounded-xl" data-testid="register-name" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input value={regForm.email} onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
                    placeholder="you@email.com" type="email" required className="pl-10 rounded-xl" data-testid="register-email" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input value={regForm.phone} onChange={(e) => setRegForm({ ...regForm, phone: e.target.value })}
                    placeholder="+91 98490 72001" className="pl-10 rounded-xl" data-testid="register-phone" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input value={regForm.password} onChange={(e) => setRegForm({ ...regForm, password: e.target.value })}
                    placeholder="Create password" type="password" required className="pl-10 rounded-xl" data-testid="register-password" />
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-terracotta hover:bg-terracotta-hover text-white rounded-xl h-11" data-testid="register-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create Account
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
